import { useApiGetMemberPaymentTransactionRequest } from '@hooks-api/fortune/useApiGetMemberPaymentTransactionRequest'
import { type CartCalculateResult } from '@hooks-api/types/dataModel/coupon'
import { useMemo } from 'react'
import { type PlatformType } from './store-map'
import { useFpB2bCartCalculate } from './use-fp-b2b-cart-calculate'

export const useFpB2bCalculateResultWithTransactionInfo = (
  campaignId: number | null,
  campaignCode: string | undefined,
  platform: PlatformType,
  repaymentTransactionId?: string,
) => {
  const shouldCalculate = repaymentTransactionId === undefined
  const {
    calculateResult,
    calculateValue,
    isLoading: isCalculateLoading,
  } = useFpB2bCartCalculate(campaignId, campaignCode, platform)

  const { destructuredData: paymentTransactionInfo, isLoading: isTransactionLoading } = useApiGetMemberPaymentTransactionRequest({ id: repaymentTransactionId || '' })
  /**
   * 為了同時支援結帳以及重新付款
   * 1. 預設填入原本的 calculate API 回傳值
   * 2. 把 transaction response 的資料格式轉換成 calculate API 的回傳格式
   * 3. 有 transaction 時 calculate API 就不會有回傳值因此可以直接取代
   */
  const calculateResultWithTransactionInfo = useMemo(() => {
    if (shouldCalculate) return calculateResult
    if (paymentTransactionInfo) {
      const transactionInfo: CartCalculateResult = {
        cart_uuid: '', // 重新付款不會有 cart_uuid
        shipping_methods: paymentTransactionInfo.shipping_method ? [paymentTransactionInfo.shipping_method] : [],
        applied_shipping_method_id: paymentTransactionInfo.shipping_method?.shipping_method_id,
        applied_payment_method_id: paymentTransactionInfo.payment_method?.id,
        shipping_total: paymentTransactionInfo.shipping_total,
        shipping_subtotal: paymentTransactionInfo.shipping_subtotal,
        total: paymentTransactionInfo.total,
        subtotal: paymentTransactionInfo.subtotal,
        tax: paymentTransactionInfo.tax,
        total_after_discount: paymentTransactionInfo.total_after_discount,
        total_after_shipping: paymentTransactionInfo.total_after_shipping,
        discount_items: paymentTransactionInfo.discount_items,
        payment_methods: paymentTransactionInfo.payment_methods,
        order_items: paymentTransactionInfo.child_orders?.flatMap(childOrder => childOrder.order_items.map(orderItem => ({
          sku: orderItem.sku,
          sale_price: orderItem.sale_price,
          applied_coupon_ids: [],
          delivery_class: childOrder.delivery_class,
          id: orderItem.id,
          quantity: orderItem.quantity,
          is_addon_v2: orderItem.is_addon_v2,
          addon_v2_price: orderItem.addon_v2_price,
          parent_product_id: orderItem.parent_product_id || null,
          addon_main_product_id: orderItem.addon_main_product_id || null,
        }))) || [],
        applied_coupons: [],
        cart_validation_messages: [],
        notice_info: [],
        giveaways: [],
        addon_v2_total: paymentTransactionInfo.addon_v2_total,
        addon_v2_total_before_discount: paymentTransactionInfo.addon_v2_total_before_discount,
      }
      return transactionInfo
    }
    return undefined
  }, [calculateResult, paymentTransactionInfo, shouldCalculate])

  return {
    calculateResultWithTransactionInfo,
    paymentTransactionInfo,
    calculateResult,
    calculateValue,
    isLoading: isCalculateLoading || isTransactionLoading,
  }
}
